import { useEffect } from "react";
import useCommon from "hooks/useCommon";
import {
  getDashboardGuestSections,
  getDashboardDailyTasks,
  resetDashboardGuestSectionsState,
  resetDashboardDailyTasksState,
} from "store/slice/dashboardSlice";
import { Alert, Button, Box, Card, Grid, Typography } from "@mui/material";
import GuestBadges from "components/GuestBadges";
import dayjs from "utils/dayjs";
import colors from "assets/theme/base/colors";
import { GuestProfileName } from "components/GuestProfileDrawer";
import RoomStatusAlerts from "./RoomStatusAlerts";

const sections = [
  {
    key: "todayArrivals",
    title: "Today's Arrival",
    color: "#547d9e",
    route: "/arrivals&departures?tab=arrivals",
  },
  { key: "activeStays", title: "Active Stays", color: "#47856b", route: "/inHouseGuests" },
  {
    key: "todayDepartures",
    title: "Today's Departure",
    color: "#a07b33",
    route: "/arrivals&departures?tab=departures",
  },
];

function GuestCard({ guest, hotelId, timezone, color }) {
  const name = guest.guestName || "Guest name unavailable";
  const time =
    guest.arrivalTimeLocal ||
    (guest.arrivalTime && timezone && dayjs(guest.arrivalTime).isValid()
      ? dayjs(guest.arrivalTime).tz(timezone).format("h:mm A")
      : "Time unavailable");
  return (
    <Box
      sx={{
        p: 1,
        borderRadius: "12px",
        bgcolor: colors.light.main,
        border: `1px solid ${colors.primary.main}40`,
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Box minWidth={0} width="100%">
          <Box display="flex" flexDirection="column" gap={0}>
            <GuestProfileName
              guestId={guest.guestId}
              hotelId={hotelId}
              name={name}
              sx={{ minWidth: 0, fontSize: 12, fontWeight: 400, overflowWrap: "anywhere" }}
            />
            <Typography sx={{ fontSize: 10, opacity: 0.75, overflowWrap: "anywhere" }}>
              Room {guest.roomNumber || "unassigned"}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 10, opacity: 0.75, mt: 0.3 }}>Arrival · {time}</Typography>
          {guest.stays != null && (
            <Typography sx={{ fontSize: 10, opacity: 0.75 }}>
              {guest.stays} {guest.stays === 1 ? "stay" : "stays"}
            </Typography>
          )}
        </Box>
      </Box>
      <GuestBadges badges={guest.badges} color={color} />
    </Box>
  );
}

function useDashboardSection(hotelId, key, fetchData, resetState) {
  const { DISPATCH, STORE } = useCommon();
  const dashboard = STORE((state) => state.dashboard);

  useEffect(() => {
    if (hotelId) DISPATCH(fetchData({ hotelId }));
    else DISPATCH(resetState());
    return () => {
      DISPATCH(resetState());
    };
  }, [DISPATCH, hotelId, fetchData, resetState]);

  const currentHotel = hotelId && dashboard[key + "HotelId"] === hotelId;

  return {
    data: currentHotel ? dashboard[key] : null,
    loading: currentHotel ? dashboard[key + "Loading"] : Boolean(hotelId),
    error: currentHotel ? dashboard[key + "Error"] : null,
    retry: () => {
      if (hotelId) DISPATCH(fetchData({ hotelId }));
    },
  };
}

function SectionStatus({ hotelId, state, empty, hasItems }) {
  if (!hotelId)
    return <Typography sx={{ fontSize: 12 }}>Select a hotel to view today's activity.</Typography>;
  if (state.loading)
    return (
      <Typography role="status" sx={{ fontSize: 12 }}>
        Loading...
      </Typography>
    );
  if (state.error)
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={state.retry}>
            Retry
          </Button>
        }
      >
        {state.error}
      </Alert>
    );
  if (!hasItems) return <Typography sx={{ fontSize: 12, opacity: 0.7 }}>{empty}</Typography>;
  return null;
}

export default function TodayOverview() {
  const { STORE } = useCommon();
  const hotelId = STORE((state) => state.ui.hotelId);

  const guestState = useDashboardSection(
    hotelId,
    "guestSections",
    getDashboardGuestSections,
    resetDashboardGuestSectionsState
  );
  const taskState = useDashboardSection(
    hotelId,
    "dailyTasks",
    getDashboardDailyTasks,
    resetDashboardDailyTasksState
  );

  const today = guestState.data?.todayDateLocal || taskState.data?.todayDateLocal;

  return (
    <Grid container spacing="18px" sx={{ color: colors.dark.main }}>
      <Grid item xs={12} lg={8}>
        <Card sx={{ height: "100%", minHeight: 520, p: 1.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography sx={{ fontSize: 17, fontWeight: 600 }}>Today’s Guests</Typography>
            <Typography sx={{ fontSize: 11, opacity: 0.7 }}>
              {today ? dayjs(today).format("MMM D, YYYY") : ""}
            </Typography>
          </Box>
          <Grid container spacing={1.5}>
            {sections.map((section) => {
              const state = guestState.data?.sections?.find((item) => item.key === section.key);
              const guests = state?.guests || [];
              return (
                <Grid item xs={12} md={4} key={section.key}>
                  <Box
                    sx={{
                      p: 1,
                      height: "100%",
                      minHeight: 420,
                      borderRadius: "14px",
                      bgcolor: "rgba(27,46,36,0.035)",
                      border: "1px solid rgba(27,46,36,0.12)",
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                      mb={1.4}
                    >
                      <Box display="flex" alignItems="center" gap={0.7}>
                        <Box
                          sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: section.color }}
                        />
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                          {section.title}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 9, opacity: 0.65 }}>
                        {guestState.loading || guestState.error || !hotelId
                          ? "—"
                          : (state?.count ?? guests.length)}{" "}
                        TOTAL
                      </Typography>
                    </Box>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={1}
                      sx={{
                        maxHeight: 380,
                        overflowY: "auto",
                        pr: 0.5,
                        "& > *": { flexShrink: 0 },
                      }}
                    >
                      <SectionStatus
                        hotelId={hotelId}
                        state={guestState}
                        empty="No guests in this section today."
                        hasItems={guests.length > 0}
                      />
                      {guests.map((guest) => (
                        <GuestCard
                          key={guest.reservationId}
                          guest={guest}
                          hotelId={hotelId}
                          timezone={guestState.data.timezone}
                          color={section.color}
                        />
                      ))}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Card>
      </Grid>
      <Grid item xs={12} lg={4}>
        <Card sx={{ height: "100%", minHeight: 520, p: 2 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 600, mb: 2 }}>Today’s Alerts</Typography>
          {/* <Typography sx={{ fontSize: 11, opacity: 0.7, mt: 0.5, mb: 2 }}>
            Recent room status updates, maintenance tasks and pending requests
          </Typography> */}
          <Box sx={{ maxHeight: 430, overflowY: "auto", pr: 0.5 }}>
            {[
              {
                key: "todaysMaintenanceTasks",
                title: "Today's Maintenance Tasks",
                color: "#ab493f",
                empty: "No maintenance tasks scheduled today.",
              },
              {
                key: "pendingRequests",
                title: "Pending Requests",
                color: "#947024",
                empty: "No pending requests due today.",
              },
            ].map((section) => {
              const state = taskState.data?.sections?.find((item) => item.key === section.key);
              const items = state?.items || [];
              return (
                <Box key={section.key} mb={1}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: section.color, mb: 0.5 }}>
                    {section.title}
                    {` (${taskState.loading || taskState.error || !hotelId ? "—" : (state?.count ?? items.length)})`}
                  </Typography>
                  <SectionStatus
                    hotelId={hotelId}
                    state={taskState}
                    empty={section.empty}
                    hasItems={items.length > 0}
                  />
                  {items.map((item) => (
                    <Box
                      key={item._id}
                      sx={{
                        p: 1.2,
                        mt: 1,
                        borderRadius: "12px",
                        bgcolor: colors.light.main,
                        border: `1px solid ${colors.primary.main}40`,
                      }}
                    >
                      <Typography sx={{ fontSize: 12, lineHeight: 1.6 }}>
                        {item.title || "Untitled task"}
                      </Typography>
                      {section.key === "pendingRequests" ? (
                        <Box sx={{ mt: 0.4 }}>
                          <Typography sx={{ fontSize: 10, opacity: 0.7 }}>
                            Guest: {item.guestName || "Not available"}
                          </Typography>
                          <Typography sx={{ fontSize: 10, opacity: 0.7 }}>
                            Assigned to:{" "}
                            {item.assignedUsers
                              ?.map((user) => user.name)
                              .filter(Boolean)
                              .join(", ") || "Unassigned"}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: 10, opacity: 0.7 }}>
                          {[
                            item.category && `Category: ${item.category}`,
                            item.priority && `Priority: ${item.priority}`,
                            item.status && `Status: ${item.status}`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              );
            })}
            <RoomStatusAlerts hotelId={hotelId} />
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
}
